import React, { useState, useMemo, useEffect, useContext } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import ReactApexChart from "react-apexcharts";
import {
  Card,
  CardContent,
  Box,
  Typography,
  Button,
  TextField,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Chip,
  Breadcrumbs,
  Link,
  TablePagination,
  MenuItem,
  FormControl,
  Select,
  InputLabel,
  CircularProgress,
} from "@mui/material";
import { CSVLink } from "react-csv";
import { AuthContext } from "../../../../authentication/AuthContext";
import analyticsService from "../../../../services/analyticsService";
import { getDateRangeFromFilter } from "../../../../utils/dateFilterUtils";

const ProductRecommendationFunnelDetails = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useContext(AuthContext);
  const dateFilterValue = location.state?.dateFilterValue;
  const [loading, setLoading] = useState(true);
  const [funnelData, setFunnelData] = useState([]);
  const [showDateFilter] = useState(false); // Hidden on detail page

  // SEARCH FILTER
  const [search, setSearch] = useState("");

  // TABLE FILTERS
  const [stageFilter, setStageFilter] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [minValue, setMinValue] = useState("");
  const [maxValue, setMaxValue] = useState("");

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  // Fetch funnel data
  useEffect(() => {
    const fetchData = async () => {
      try {
        if (!user?.client_id) {
          console.warn("⚠️ Client ID not available");
          setLoading(false);
          return;
        }

        // Convert filter to actual dates (with default if not provided)
        const { start_date, end_date } = getDateRangeFromFilter(dateFilterValue || "Last 3 months");

        const response = await analyticsService.getProductRecommendationFunnel(
          user.client_id,
          start_date,
          end_date
        );

        if (response.success && response.data && response.data.length > 0) {
          const data = response.data[0];
          // Transform data for table display
          const tableData = [
            { stage: "Suggested", value: data.suggested_count || 0, category: "Suggested" },
            { stage: "Clicked", value: data.clicked_count || 0, category: "Clicked" },
            { stage: "Purchased", value: data.purchased_count || 0, category: "Purchased" },
          ];
          setFunnelData(tableData);
          console.log("✅ Funnel detail data loaded:", tableData);
        } else {
          console.warn("⚠️ No funnel data returned");
        }
      } catch (error) {
        console.error("❌ Error fetching funnel data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [user?.client_id, dateFilterValue]);

  // FILTER LOGIC
  const filteredData = useMemo(() => {
    return funnelData.filter((row) => {
      const matchSearch = row.stage
        .toLowerCase()
        .includes(search.toLowerCase());

      const matchStage =
        stageFilter === "" || row.stage === stageFilter;

      const matchCategory =
        categoryFilter === "" || row.category === categoryFilter;

      const matchMin =
        minValue === "" || row.value >= Number(minValue);

      const matchMax =
        maxValue === "" || row.value <= Number(maxValue);

      return (
        matchSearch && matchStage && matchCategory && matchMin && matchMax
      );
    });
  }, [search, stageFilter, categoryFilter, minValue, maxValue, funnelData]);
 
  const getCategoryChip = (category) => {
    const colors = {
      Views: "info",
      Cart: "warning",
      Purchase: "success",
    };
    return (
      <Chip
        label={category}
        color={colors[category]}
        size="small"
        sx={{ fontWeight: 600 }}
      />
    );
  };
 
  // Get chart data from funnelData
  const suggested = funnelData[0]?.value || 0;
  const clicked = funnelData[1]?.value || 0;
  const purchased = funnelData[2]?.value || 0;

  const chartState = {
    series: [
      {
        name: "Funnel Series",
        data: [suggested, clicked, purchased],
      },
    ],
    options: {
      chart: {
        type: "bar",
        height: 350,
        toolbar: { show: false },
      },

      plotOptions: {
        bar: {
          horizontal: true,
          isFunnel: true,
          barHeight: "48%",
          borderRadius: 6,
        },
      },

      fill: {
        type: "solid",
        colors: ["#6f42c1", "#6f42c1", "#6f42c1"],
      },

      dataLabels: {
        enabled: true,
        formatter: function (val, opt) {
          const values = [suggested, clicked, purchased];
          return values[opt.dataPointIndex] || 0;
        },
        style: {
          fontSize: "16px",
          fontWeight: 700,
          colors: ["#ffffff"],
        },
      },

      xaxis: {
        categories: [
          "Suggested",
          "Clicked",
          "Purchased",
        ],
        max: suggested || 1500,
        labels: { show: false },
      },

      yaxis: {
        labels: { show: false },
      },

      grid: { show: false },
      legend: { show: false },
      tooltip: { enabled: false },
    },
  };
 
  return (
    <Box p={3}>

      {/* Back */}
      <Box mb={2}>
        <Button
          variant="outlined"
          onClick={() => navigate(-1)}
          sx={{
            borderRadius: 2,
            textTransform: "none",
            borderColor: "#6f42c1",
            color: "#6f42c1",
            "&:hover": { borderColor: "#5b34a0", color: "#5b34a0" },
          }}
        >
          ← Back
        </Button>
      </Box>

      {/* Chart or Loading */}
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "400px" }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {/* ⭐ FUNNEL GRAPH WITH REAL DATA */}
          <Card sx={{ borderRadius: 3, boxShadow: "0 4px 12px rgba(0,0,0,0.08)", mb: 3 }}>
            <CardContent>
              <ReactApexChart
                options={chartState.options}
                series={chartState.series}
                type="bar"
                height={350}
              />

              <Box
                display="flex"
                justifyContent="space-around"
                mt={1.5}
                sx={{ fontSize: "14px", color: "#525252", fontWeight: 600 }}
              >
                <span>Suggested</span>
                <span>Clicked</span>
                <span>Purchased</span>
              </Box>
            </CardContent>
          </Card>
 
      {/* SEARCH + FILTERS + EXPORT */}
      <Box display="flex" flexWrap="wrap" gap={2} mb={2}>
        {/* SEARCH */}
        <TextField
          label="Search Stage"
          variant="outlined"
          size="small"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ width: 200 }}
        />
 
        {/* STAGE FILTER */}
        <FormControl size="small" sx={{ width: 200 }}>
          <InputLabel>Stage</InputLabel>
          <Select
            value={stageFilter}
            label="Stage"
            onChange={(e) => setStageFilter(e.target.value)}
          >
            <MenuItem value="">All</MenuItem>
            {funnelData.map((d) => (
              <MenuItem key={d.stage} value={d.stage}>
                {d.stage}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
 
        {/* EXPORT */}
        <Button
          variant="contained"
          sx={{
            backgroundColor: "#6f42c1",
            "&:hover": { backgroundColor: "#5b34a0" },
            textTransform: "none",
            borderRadius: "8px",
          }}
        >
          <CSVLink
            data={filteredData}
            filename={"purchase_funnel.csv"}
            style={{ color: "#fff", textDecoration: "none" }}
          >
            Export CSV
          </CSVLink>
        </Button>
      </Box>
 
          {/* TABLE */}
          <Card sx={{ borderRadius: 3, boxShadow: "0 4px 12px rgba(0,0,0,0.08)" }}>
            <CardContent>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: "#f3e8ff" }}>
                    <TableCell><strong>Stage</strong></TableCell>
                    <TableCell><strong>Value</strong></TableCell>
                    <TableCell><strong>Category</strong></TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {filteredData
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((row, idx) => (
                      <TableRow key={idx}>
                        <TableCell>{row.stage}</TableCell>
                        <TableCell>{row.value}</TableCell>
                        <TableCell>{getCategoryChip(row.category)}</TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>

              <TablePagination
                component="div"
                count={filteredData.length}
                page={page}
                onPageChange={(e, newPage) => setPage(newPage)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => {
                  setRowsPerPage(parseInt(e.target.value, 10));
                  setPage(0);
                }}
                rowsPerPageOptions={[5, 10, 25]}
              />
            </CardContent>
          </Card>
        </>
      )}
    </Box>
  );
};
 
export default ProductRecommendationFunnelDetails;